// src/pages/MeineRezeptPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./MeineRezeptPage.css";
import { X } from "lucide-react";

import { getCurrentAuthUser } from "../services/authDataService";
import {
  getRecipesData,
  updateRecipeData,
  deleteRecipeData,
} from "../services/recipeDataService";

import GateIcon from "../components/GateIcon";
import LoginHintModal from "../components/LoginHintModal";
import SpeechIcon from "../components/SpeechIcon";

import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { jsPDF } from "jspdf";

function MeineRezeptPage() {
  const navigate = useNavigate();

  const [rezepte, setRezepte] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [rezeptToEdit, setRezeptToEdit] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");

  const [rezeptToDelete, setRezeptToDelete] = useState(null);
  const [rezeptToShow, setRezeptToShow] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [showLoginHint, setShowLoginHint] = useState(false);

  const activeRecipe = rezepte.find((r) => r.id === rezeptToShow);
  const rezeptePerPage = 20;
  const totalPages = Math.max(1, Math.ceil(rezepte.length / rezeptePerPage) || 1);
  const currentRezepte = rezepte.slice(
    (currentPage - 1) * rezeptePerPage,
    currentPage * rezeptePerPage
  );

  useEffect(() => {
  const checkLogin = async () => {
    try {
      const authUser = await getCurrentAuthUser();

      if (!authUser?.uid) {
        setShowLoginHint(true);
        setIsLoading(false);
      }
    } catch (err) {
      console.warn("Fehler bei Login-Check auf MeineRezeptPage:", err);
      setShowLoginHint(true);
      setIsLoading(false);
    }
  };

  checkLogin();
}, []);

  useEffect(() => {
  const loadRezepte = async () => {
    try {
      const user = await getCurrentAuthUser();

      if (!user?.uid) {
        setRezepte([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const loadedRezepte = await getRecipesData(user.uid);

      setRezepte(Array.isArray(loadedRezepte) ? loadedRezepte : []);
      setCurrentPage(1);
    } catch (err) {
      console.error("Fehler beim Laden der Rezepte:", err);
      setRezepte([]);
    } finally {
      setIsLoading(false);
    }
  };

  loadRezepte();
}, []);

  const startEdit = (docId) => {
    const rezept = rezepte.find((r) => r.id === docId);
    if (!rezept) return;

    setRezeptToEdit(docId);
    setEditTitle(rezept.title || "Neues Rezept");
    setEditContent(rezept.content || rezept.text || "");
  };

const confirmEdit = async () => {
  try {
    const user = await getCurrentAuthUser();
    if (!user?.uid || !rezeptToEdit) return;

    await updateRecipeData(user.uid, rezeptToEdit, {
      title: editTitle,
      content: editContent,
    });

    setRezepte((prev) =>
      prev.map((r) =>
        r.id === rezeptToEdit
          ? { ...r, title: editTitle, content: editContent }
          : r
      )
    );

    setRezeptToEdit(null);
  } catch (err) {
    console.error("Fehler beim Speichern des Rezepts:", err);
    alert("Speichern fehlgeschlagen. Bitte nochmal versuchen.");
  }
};

  const confirmDelete = async () => {
    try {
      const user = await getCurrentAuthUser();
if (!user?.uid || !rezeptToDelete) return;

await deleteRecipeData(user.uid, rezeptToDelete);

     setRezepte((prev) => {
  const updated = prev.filter((r) => r.id !== rezeptToDelete);

  const newTotalPages = Math.max(
    1,
    Math.ceil(updated.length / rezeptePerPage) || 1
  );

  setCurrentPage((p) => Math.min(p, newTotalPages));

  return updated;
});

setRezeptToDelete(null);
    } catch (err) {
      console.error("Fehler beim Löschen (deleteDoc):", err);
      alert("Löschen fehlgeschlagen. Bitte nochmal versuchen.");
    }
  };

  const toSafeFileName = (title) => {
    return (
      title
        ?.replace(/[^\w\säöüÄÖÜß-]/g, "")
        .replace(/\s+/g, "_")
        .trim() || "rezept"
    );
  };

  const makePdfFriendlyText = (text = "") => {
  return text
    .replace(/🍽️|🍴|🍽/g, "")
    .replace(/⏱️|⏰|⏱/g, "")
    .replace(/🛒/g, "")
    .replace(/👨‍🍳|👩‍🍳|🧑‍🍳/g, "")
    .replace(/🌱/g, "")
    .replace(/✅|❌|⭐|✨|📌|📄|🖨️/g, "")
    .replace(/[ ]{2,}/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/[\u{1F300}-\u{1FAFF}]/gu, "")
    .replace(/[\u{2600}-\u{27BF}]/gu, "")
    .replace(/[\u{FE00}-\u{FE0F}]/gu, "")
    .replace(/[\u{200D}]/gu, "")
    .replace(/Ø‹ß}|Ø¬ì|Ø=ÞÒ|Ø=Üi|Ø=ß|Ø>ÝW|Ø<ß/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

  const printRecipe = async (rezept) => {
    const title = rezept.title || "Rezept";
    const content = rezept.content || rezept.text || "";

    if (!Capacitor.isNativePlatform()) {
      const printWindow = window.open("", "", "width=800,height=600");
      if (!printWindow) {
        alert("Druckfenster konnte nicht geöffnet werden.");
        return;
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              body {
                font-family: "Times New Roman", serif;
                padding: 40px;
                line-height: 1.6;
                font-size: 14pt;
                color: black;
              }
              h1 {
                font-size: 24pt;
                margin-bottom: 20px;
              }
              pre {
                white-space: pre-wrap;
                font-family: "Times New Roman", serif;
              }
              .footer-note {
                margin-top: 30px;
                font-style: italic;
                font-size: 10pt;
                text-align: center;
              }
            </style>
          </head>
          <body>
            <h1>${title}</h1>
            <pre>${content}</pre>
            <div class="footer-note">
              🌱 Dieses Rezept stammt von Edens Ernte – Gemeinsam nachhaltig teilen.
            </div>
            <script>
              window.onload = function() {
                window.print();
                setTimeout(() => window.close(), 100);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
      return;
    }

    try {
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
  });

  const pdfContent = makePdfFriendlyText(content);
  const pdfFooter =
    "Dieses Rezept stammt von Edens Ernte – Gemeinsam nachhaltig teilen.";

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 40;
  const maxWidth = pageWidth - margin * 2;

  let y = margin;

  const ensurePageSpace = (neededHeight = 18) => {
    if (y + neededHeight > pageHeight - margin) {
      pdf.addPage();
      y = margin;
    }
  };

  // Einheitliche Schrift
  pdf.setFont("times", "bold");
  pdf.setFontSize(22);
  pdf.text(title, margin, y);
  y += 32;

  pdf.setFont("times", "normal");
  pdf.setFontSize(12);

  // Text in Absätze / Zeilen aufteilen und bewusst rendern
 
const lines = pdfContent.split("\n");

lines.forEach((line) => {
  const trimmed = line.trim();

  // Leerzeile = Abstand
  if (!trimmed) {
    y += 10;
    return;
  }

  // 👉 Headline erkennen
  const lower = trimmed.toLowerCase();

const isHeadline =
  lower === "zutaten" ||
  lower === "zubereitung" ||
  lower === "tipps" ||
  lower === "variationen";

  if (isHeadline) {
    ensurePageSpace(20);
    pdf.setFont("times", "bold");
    pdf.setFontSize(14);
    pdf.text(trimmed, margin, y);
    pdf.setFont("times", "normal");
    pdf.setFontSize(12);
    y += 20;
    return;
  }

  // 👉 normale Zeilen
  const split = pdf.splitTextToSize(trimmed, maxWidth);

  split.forEach((subLine) => {
    ensurePageSpace(18);
    pdf.text(subLine, margin, y);
    y += 16;
  });
});

  // etwas Abstand vor Footer
  y += 12;

  const footerLines = pdf.splitTextToSize(pdfFooter, maxWidth - 20);

  if (y + footerLines.length * 14 > pageHeight - margin) {
    pdf.addPage();
    y = margin;
  }

  pdf.setFont("times", "bolditalic");
pdf.setFontSize(13);

footerLines.forEach((line) => {
  pdf.text(line, pageWidth / 2, y, { align: "center" });
  y += 16;
});

  const safeTitle = toSafeFileName(title);
  const fileName = `${safeTitle}.pdf`;

  const pdfBase64 = pdf.output("datauristring").split(",")[1];

  await Filesystem.writeFile({
    path: fileName,
    data: pdfBase64,
    directory: Directory.Cache,
  });

  const fileUri = await Filesystem.getUri({
    directory: Directory.Cache,
    path: fileName,
  });

  await Share.share({
    title,
    text: "Hier ist dein Rezept",
    url: fileUri.uri,
    dialogTitle: "Rezept teilen",
  });
} catch (err) {
  console.error("Fehler beim PDF-Erstellen:", err);
  alert("Fehler beim Erstellen des PDFs.");
}
  };

  const handleClose = () => setRezeptToShow(null);

  return (
    <div className="forum-container meine-rezept-page">
      {showLoginHint && (
        <LoginHintModal onClose={() => setShowLoginHint(false)} />
      )}

      <img
        src="/logo-edens-ernte-transparent.png"
        alt="Logo"
        className="browse-logo"
        onClick={() => navigate("/")}
      />
      <GateIcon className="login-icon" />

      <img
        src="/rezept-icon.png"
        alt="Rezepthelfer Icon"
        className="rezept-icon"
        onClick={() => navigate("/rezepte")}
      />
      <SpeechIcon className="speech-icon no-tap-highlight" />

      <p className="top-claim">Deine gespeicherten Rezepte</p>

      <div className="forum-main-box">
        {isLoading ? (
          <p>Lade Rezepte…</p>
        ) : currentRezepte.length === 0 ? (
          <p>Du hast noch keine Rezepte gespeichert.</p>
        ) : (
          <>
            {currentRezepte.map((rezept) => {
              const isEditing = rezept.id === rezeptToEdit;

              const title = rezept.title || "Neues Rezept";
              const contentPreview =
                (rezept.content || rezept.text || "")
                  .split("\n")
                  .slice(0, 5)
                  .join(" ")
                  .trim() + "...";

              if (isEditing) {
                return (
                  <div key={rezept.id} className="post-box compact-post">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="edit-input"
                    />
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="edit-textarea"
                    />
                    <button className="rezept-green-button" onClick={confirmEdit}>
                      Speichern
                    </button>
                  </div>
                );
              }

              return (
                <div
                  key={rezept.id}
                  className="rezept-box compact-post hoverable"
                  onClick={() => setRezeptToShow(rezept.id)}
                >
                  <div
                    className="post-actions-top-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="my-edit-icon" onClick={() => startEdit(rezept.id)}>
                      ✏️
                    </span>
                    <span
                      className="my-delete-icon"
                      onClick={() => setRezeptToDelete(rezept.id)}
                    >
                      🗑️
                    </span>
                  </div>

                  <h3>{title}</h3>
                  <p>{contentPreview}</p>
                </div>
              );
            })}

            <div className="pagination">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                ⏮
              </button>
              <button onClick={() => setCurrentPage((p) => Math.max(p - 10, 1))}>
                «
              </button>
              <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}>
                ‹
              </button>
              <span>
                {currentPage} / {totalPages}
              </span>
              <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}>
                ›
              </button>
              <button onClick={() => setCurrentPage((p) => Math.min(p + 10, totalPages))}>
                »
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                ⏭
              </button>
            </div>
          </>
        )}
      </div>

      {rezeptToDelete !== null && (
        <div className="modal-overlay" onClick={() => setRezeptToDelete(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Rezept wirklich löschen?</h3>
            <p>Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="modal-buttons">
              <button className="delete-btn" onClick={confirmDelete}>
                Löschen
              </button>
              <button className="cancel-btn" onClick={() => setRezeptToDelete(null)}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {rezeptToShow !== null && activeRecipe && (
  <div className="recipe-overlay" onClick={() => setRezeptToShow(null)}>
    <div className="recipe-popup" onClick={(e) => e.stopPropagation()}>
      <pre className="recipe-output">
        {activeRecipe.content || activeRecipe.text || ""}
      </pre>

            <div className="rezepte-button-row">
              <button
                className="rezepte-green-button"
                onClick={() => printRecipe(activeRecipe)}
              >
                <span className="icon">🖨️</span> Rezept drucken
              </button>

              <button className="rezepte-red-button" onClick={handleClose}>
                <span className="icon-label">
                  <X size={24} strokeWidth={3} />
                  <span className="label-text">Rezept schließen</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MeineRezeptPage;